'use client';

import { useState, useEffect, useCallback } from 'react';
import { formatDiamond } from '@/utils/formatDiamond';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography } from '@mui/material';
import { Icon } from '@/components/icons';
import { APP_MAX_WIDTH } from '@/components/PageContent/PageContent';
import { Z_INDEX } from '@/constants/zIndex';
import {
  TEMP_FG,
  TEMP_FG_INVERSE,
  TEMP_FG_PRIMARY,
  TEMP_FG_DIAMOND,
  TEMP_BG_INSET_NEUTRAL,
} from '@/constants/tempColors';
import {
  TEMP_TEXT_BOLD_XXL,
  TEMP_TEXT_SEMIBOLD_L,
  TEMP_TEXT_SEMIBOLD_XL,
  TEMP_TEXT_BOLD_XL,
} from '@/constants/tempTypo';
import { NetworkManager, request, axiosMethod } from '@/api/http';
import { useTranslator } from '@/lang';

const EASING: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

interface WelcomeFlowProps {
  /** 온보딩 직후(가입 축하 포함)인지, 기존 유저 복귀(출석만)인지 */
  showWelcome: boolean;
  onDone: () => void;
}

const instance = new NetworkManager('rizzling').getInstance();

interface ApiResponse<T> {
  status: number;
  message: string;
  data: T;
}

interface AttendanceResult {
  rewardAmount: number;
}

interface BalanceResult {
  totalAmount: number;
}

const ATTENDANCE_CHECKED_KEY = 'museling_attendance_date';

function hasCheckedToday(): boolean {
  if (typeof window === 'undefined') return true;
  const stored = localStorage.getItem(ATTENDANCE_CHECKED_KEY);
  const today = new Date().toISOString().slice(0, 10);
  return stored === today;
}

function markCheckedToday() {
  const today = new Date().toISOString().slice(0, 10);
  localStorage.setItem(ATTENDANCE_CHECKED_KEY, today);
}

export default function WelcomeFlow({ showWelcome, onDone }: WelcomeFlowProps) {
  // 출석 자동 모달은 설정-출석체크 페이지로 이전됨. 가입축하 모달만 노출.
  const [phase, setPhase] = useState<'welcome' | 'attendance' | 'done'>(
    showWelcome ? 'welcome' : 'done',
  );
  const [attendanceReward, setAttendanceReward] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [attendanceError, setAttendanceError] = useState(false);

  // 출석 체크 API 호출
  const doAttendance = useCallback(async () => {
    if (hasCheckedToday()) {
      setPhase('done');
      onDone();
      return;
    }

    try {
      const [attendRes, balRes] = await Promise.all([
        request<ApiResponse<AttendanceResult>>(instance, axiosMethod.POST, '/event/attendance'),
        request<ApiResponse<BalanceResult>>(instance, axiosMethod.GET, '/payment/balance'),
      ]);
      setAttendanceReward(attendRes.data.data.rewardAmount);
      setBalance(balRes.data.data.totalAmount);
      markCheckedToday();
    } catch {
      // 이미 출석했거나 에러 → 바로 완료
      markCheckedToday();
      setAttendanceError(true);
      setPhase('done');
      onDone();
    }
  }, [onDone]);

  useEffect(() => {
    if (phase === 'attendance') {
      doAttendance();
    }
  }, [phase, doAttendance]);

  useEffect(() => {
    if (phase === 'done') {
      onDone();
    }
  }, [phase, onDone]);

  const handleWelcomeDone = () => {
    // 출석 자동 모달은 더 이상 노출하지 않음 — 설정-출석체크에서 받음.
    setPhase('done');
  };

  const handleAttendanceDone = () => {
    setPhase('done');
  };

  if (phase === 'done') return null;

  return (
    <AnimatePresence>
      {phase === 'welcome' && (
        <ModalBackdrop key="welcome" onClose={handleWelcomeDone}>
          <WelcomeModal onConfirm={handleWelcomeDone} />
        </ModalBackdrop>
      )}
      {phase === 'attendance' && attendanceReward !== null && !attendanceError && (
        <ModalBackdrop key="attendance" onClose={handleAttendanceDone}>
          <AttendanceModal
            reward={attendanceReward}
            balance={balance ?? 0}
            onConfirm={handleAttendanceDone}
          />
        </ModalBackdrop>
      )}
    </AnimatePresence>
  );
}

// --- 공통 모달 백드롭 ---
function ModalBackdrop({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: 'tween', duration: 0.25, ease: EASING }}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: Z_INDEX.IMAGE_VIEWER + 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: APP_MAX_WIDTH,
        margin: '0 auto',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'tween', duration: 0.25, ease: EASING }}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: 24,
          maxWidth: 335,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 32,
          paddingBottom: 20,
          paddingLeft: 20,
          paddingRight: 20,
        }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

// --- 가입 축하 모달 ---
function WelcomeModal({ onConfirm }: { onConfirm: () => void }) {
  const { t } = useTranslator();
  return (
    <>
      <Box sx={{ textAlign: 'center', mb: '20px' }}>
        <Typography sx={{ ...TEMP_TEXT_BOLD_XXL, color: TEMP_FG }}>
          {t('onboarding.welcome_title')}
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: TEMP_BG_INSET_NEUTRAL,
          borderRadius: '16px',
          py: '20px',
          px: '16px',
          textAlign: 'center',
          mb: '24px',
        }}
      >
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: TEMP_FG, lineHeight: 1.6 }}>
          {t('onboarding.welcome_reward_1')}
        </Typography>
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: TEMP_FG, lineHeight: 1.6 }}>
          {t('onboarding.welcome_reward_2')}
        </Typography>
        <Box sx={{ mt: '12px' }}>
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: TEMP_FG, lineHeight: 1.6 }}>
            {t('onboarding.welcome_desc_1')}
          </Typography>
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: TEMP_FG, lineHeight: 1.6 }}>
            {t('onboarding.welcome_desc_2')}
          </Typography>
        </Box>
      </Box>

      <Box
        onClick={onConfirm}
        sx={{
          bgcolor: TEMP_FG_PRIMARY,
          borderRadius: '16px',
          py: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
          {t('onboarding.start_service')}
        </Typography>
      </Box>
    </>
  );
}

// --- 출석 보상 모달 ---
function AttendanceModal({
  reward,
  balance,
  onConfirm,
}: {
  reward: number;
  balance: number;
  onConfirm: () => void;
}) {
  const { t, f } = useTranslator();
  return (
    <>
      <Box sx={{ textAlign: 'center', mb: '16px' }}>
        <Typography sx={{ ...TEMP_TEXT_BOLD_XXL, color: TEMP_FG }}>
          {t('attendance.daily_title')}
        </Typography>
      </Box>

      {/* 다이아 아이콘 + 수량 */}
      <Box sx={{ textAlign: 'center', mb: '4px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <Icon name="icDiamondFilled" size={24} color={TEMP_FG_DIAMOND} />
          <Typography sx={{ ...TEMP_TEXT_BOLD_XL, color: TEMP_FG }}>
            {f(t('attendance.reward_count'), [formatDiamond(reward)])}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: '#9e9e9e',
            mt: '4px',
          }}
        >
          {f(t('profile.current_balance'), [formatDiamond(balance)])}
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: TEMP_BG_INSET_NEUTRAL,
          borderRadius: '16px',
          py: '20px',
          px: '16px',
          textAlign: 'center',
          mt: '16px',
          mb: '24px',
        }}
      >
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: TEMP_FG, lineHeight: 1.6 }}>
          {f(t('attendance.granted_line1'), [reward])}
        </Typography>
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: TEMP_FG, lineHeight: 1.6 }}>
          {t('attendance.granted_line2')}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: TEMP_FG_PRIMARY,
            mt: '8px',
          }}
        >
          {t('attendance.continue_chat')}
        </Typography>
      </Box>

      <Box
        onClick={onConfirm}
        sx={{
          bgcolor: TEMP_FG_PRIMARY,
          borderRadius: '16px',
          py: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
          {t('attendance.confirm')}
        </Typography>
      </Box>
    </>
  );
}
